// usePersonalAccessTokens — the profile hub's PAT read + write surface (ADR-004).
// Per-user self-service under /api/auth/me/api-keys (Zone 3). Mirrors the
// useApiClients shape; mutations invalidate the one list key. 401/403 is the
// centralized apiClient's job (ADR-003); errors bubble to the tab's 5-state render.

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import type {
  CreatePatRequest,
  RenamePatRequest,
} from '@ems-app/schemas/api-keys/requests';
import type {
  CreatedPatResponse,
  PatsListResponse,
  PersonalAccessToken,
} from '@ems-app/schemas/api-keys/responses';
import { apiClient } from '../lib/apiClient.js';
import { refreshOnSettled } from '../lib/settled.js';

export const patKeys = {
  /** The caller's tokens — also the invalidation prefix. */
  all: ['api-keys'] as const,
};

const selectItems = (r: PatsListResponse): PersonalAccessToken[] => r.items;

export function usePersonalAccessTokens(): UseQueryResult<PersonalAccessToken[], Error> {
  return useQuery({
    queryKey: patKeys.all,
    queryFn: async () => (await apiClient.get<PatsListResponse>('/auth/me/api-keys')).data,
    select: selectItems,
  });
}

export function useCreatePat(): UseMutationResult<CreatedPatResponse, Error, CreatePatRequest> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreatePatRequest) =>
      (await apiClient.post<CreatedPatResponse>('/auth/me/api-keys', body)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: patKeys.all }),
  });
}

export function useRenamePat(): UseMutationResult<
  PersonalAccessToken,
  Error,
  { id: string; body: RenamePatRequest }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      (await apiClient.patch<PersonalAccessToken>(`/auth/me/api-keys/${id}`, body)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: patKeys.all }),
  });
}

export function useRevokePat(): UseMutationResult<PersonalAccessToken, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await apiClient.post<PersonalAccessToken>(`/auth/me/api-keys/${id}/revoke`)).data,
    onSettled: refreshOnSettled(() => void qc.invalidateQueries({ queryKey: patKeys.all })),
  });
}
